const CLIENT_TURN_TYPES = new Set(["activateAI", "user"]);
const CLIENT_TURN_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/;
let fallbackCounter = 0;

/**
 * Return whether a browser message can start an AI response.
 *
 * @param {object|null|undefined} message Browser message.
 * @returns {boolean} True for a client-authored AI turn.
 */
export function isClientTurnMessage(message) {
  return Boolean(message && CLIENT_TURN_TYPES.has(message.type));
}

/**
 * Generate a collision-resistant identifier that remains stable when the same
 * message object is retried or transformed from a suggestion into activation.
 *
 * @returns {string} Client turn identifier.
 */
export function generateClientTurnId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `turn_${globalThis.crypto.randomUUID()}`;
  }

  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    const randomHex = Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    return `turn_${randomHex}`;
  }

  fallbackCounter += 1;
  return `turn_${Date.now().toString(36)}_${fallbackCounter.toString(36)}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Add a stable client turn ID to any message that can start an AI response.
 * Existing valid IDs are preserved so reconnect and activation paths retry the
 * same logical turn instead of creating a second one.
 *
 * @param {object} message Browser message.
 * @returns {object} The same message object with an ID when applicable.
 */
export function ensureClientTurnId(message) {
  if (!isClientTurnMessage(message)) {
    return message;
  }

  if (
    typeof message.clientTurnId !== "string" ||
    !CLIENT_TURN_ID_PATTERN.test(message.clientTurnId)
  ) {
    message.clientTurnId = generateClientTurnId();
  }
  return message;
}
